import {
  BarChart3,
  BookOpen,
  ChevronDown,
  CircleGauge,
  FileClock,
  GraduationCap,
  HeartPulse,
  Library,
  LogOut,
  Megaphone,
  Menu,
  Settings,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { initials } from "../lib/format";
import { usePlatform } from "../lib/platform";
import { Button } from "./ui";

function Brand({ compact = false }: { compact?: boolean }) {
  const { data: platform, platformName } = usePlatform();
  const [first, ...rest] = platformName.split(/\s+/);
  return (
    <NavLink
      to="/"
      className={`brand ${compact ? "brand-compact" : ""}`}
      aria-label={`${platformName} 首页`}
    >
      <span className="brand-mark">
        {platform?.logoUrl ? <img src={platform.logoUrl} alt="" /> : <span />}
      </span>
      {!compact ? (
        <span>
          <strong>{first}</strong>
          {rest.length ? <small>{rest.join(" ")}</small> : null}
        </span>
      ) : null}
    </NavLink>
  );
}

export function PublicLayout() {
  const { data: platform, platformName } = usePlatform();
  useEffect(() => {
    document.title = platformName;
  }, [platformName]);
  return (
    <div className="public-layout">
      <a className="skip-link" href="#main-content">
        跳到主要内容
      </a>
      <div className="public-brand">
        <Brand />
      </div>
      <main id="main-content">
        <Outlet />
      </main>
      <footer>
        <span>© {new Date().getFullYear()} {platformName} · 私享学习空间</span>
        {platform?.icpFilingNumber && platform.icpFilingUrl ? (
          <a href={platform.icpFilingUrl} target="_blank" rel="noreferrer">
            {platform.icpFilingNumber}
          </a>
        ) : null}
      </footer>
    </div>
  );
}

export function LearnerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const immersive = location.pathname.startsWith("/learn/");
  const { data: platform, platformName } = usePlatform();
  useEffect(() => {
    document.title = platformName;
  }, [platformName]);
  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  return (
    <div className={`learner-shell ${immersive ? "is-immersive" : ""}`}>
      <a className="skip-link" href="#main-content">
        跳到主要内容
      </a>
      {!immersive ? (
        <header className="learner-header">
          <Brand />
          <nav aria-label="主导航">
            <NavLink to="/" end>
              首页
            </NavLink>
            <NavLink to="/courses">课程</NavLink>
            {user && ["OWNER", "ADMIN", "SUPPORT"].includes(user.role) ? (
              <NavLink
                to={user.role === "SUPPORT" ? "/admin/users" : "/admin"}
              >
                管理
              </NavLink>
            ) : null}
          </nav>
          <div className="user-menu-wrap">
            <button
              className="user-button"
              type="button"
              onClick={() => setMenuOpen((value) => !value)}
              aria-expanded={menuOpen}
            >
              <span className="avatar">{initials(user?.displayName)}</span>
              <span className="user-button-name">{user?.displayName}</span>
              <ChevronDown size={15} />
            </button>
            {menuOpen ? (
              <div className="user-dropdown">
                <NavLink to="/profile" onClick={() => setMenuOpen(false)}>
                  <Settings size={17} />
                  个人中心
                </NavLink>
                <button type="button" onClick={onLogout}>
                  <LogOut size={17} />
                  退出登录
                </button>
              </div>
            ) : null}
          </div>
        </header>
      ) : null}
      <main
        id="main-content"
        className={`learner-main ${immersive ? "is-immersive" : ""}`}
      >
        <Outlet />
      </main>
      {!immersive && platform?.icpFilingNumber && platform.icpFilingUrl ? (
        <footer className="site-filing">
          <a href={platform.icpFilingUrl} target="_blank" rel="noreferrer">
            {platform.icpFilingNumber}
          </a>
        </footer>
      ) : null}
      {!immersive ? (
        <nav className="mobile-nav" aria-label="移动端导航">
          <NavLink to="/" end>
            <CircleGauge />
            <span>首页</span>
          </NavLink>
          <NavLink to="/courses">
            <Library />
            <span>课程</span>
          </NavLink>
          <NavLink to="/profile">
            <Settings />
            <span>我的</span>
          </NavLink>
        </nav>
      ) : null}
    </div>
  );
}

const adminNav = [
  { to: "/admin", label: "概览", icon: CircleGauge, end: true },
  { to: "/admin/courses", label: "课程管理", icon: BookOpen },
  { to: "/admin/announcements", label: "平台公告", icon: Megaphone },
  { to: "/admin/users", label: "学员管理", icon: Users },
  { to: "/admin/analytics", label: "学习统计", icon: BarChart3 },
  { to: "/admin/audit", label: "审计日志", icon: FileClock },
  { to: "/admin/system", label: "系统状态", icon: HeartPulse },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mobileSidebar, setMobileSidebar] = useState(() =>
    window.matchMedia("(max-width: 780px)").matches,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const { platformName } = usePlatform();
  useEffect(() => {
    document.title = `${platformName} · 管理控制台`;
  }, [platformName]);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 780px)");
    const sync = () => setMobileSidebar(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);
  useEffect(() => {
    if (!mobileSidebar || !open) return;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileSidebar, open]);
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;
    if (mobileSidebar && !open) sidebar.setAttribute("inert", "");
    else sidebar.removeAttribute("inert");
  }, [mobileSidebar, open]);
  const closeSidebar = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus)
      window.requestAnimationFrame(() => triggerRef.current?.focus());
  };
  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  const visibleNav =
    user?.role === "SUPPORT"
      ? adminNav.filter((item) =>
          ["/admin/courses", "/admin/users", "/admin/audit"].includes(item.to),
        )
      : adminNav;
  return (
    <div className="admin-shell">
      <a className="skip-link" href="#main-content">
        跳到主要内容
      </a>
      <aside
        ref={sidebarRef}
        className={`admin-sidebar ${open ? "is-open" : ""}`}
        aria-hidden={mobileSidebar && !open ? true : undefined}
      >
        <div className="admin-sidebar-head">
          <Brand />
          <button
            ref={closeRef}
            className="icon-button sidebar-close"
            type="button"
            onClick={() => closeSidebar(true)}
            aria-label="关闭导航"
          >
            <X size={21} />
          </button>
        </div>
        <div className="admin-context">
          <ShieldCheck size={17} />
          <span>管理控制台</span>
        </div>
        <nav aria-label="管理导航">
          {visibleNav.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => closeSidebar()}>
              <Icon size={19} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="admin-sidebar-foot">
          <NavLink to="/">
            <GraduationCap size={18} />
            返回学习端
          </NavLink>
          <div className="mini-user">
            <span className="avatar">{initials(user?.displayName)}</span>
            <span>
              <strong>{user?.displayName}</strong>
              <small>{user?.email}</small>
            </span>
          </div>
        </div>
      </aside>
      {open ? (
        <button
          className="sidebar-scrim"
          aria-label="关闭导航"
          onClick={() => closeSidebar(true)}
        />
      ) : null}
      <div className="admin-content">
        <header className="admin-topbar">
          <button
            ref={triggerRef}
            className="icon-button sidebar-trigger"
            type="button"
            onClick={() => setOpen(true)}
            aria-label="打开导航"
          >
            <Menu size={22} />
          </button>
          <span>
            {visibleNav.find((item) =>
              item.end
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to),
            )?.label || "课程管理"}
          </span>
          <div className="admin-account-actions">
            <span className="admin-topbar-role">
              {user?.role === "SUPPORT"
                ? "支持人员"
                : user?.role === "OWNER"
                  ? "所有者"
                  : "管理员"}
            </span>
            <NavLink to="/profile" aria-label="账号与安全">
              <Settings size={16} />
              <span>账号与安全</span>
            </NavLink>
            <button type="button" onClick={onLogout} aria-label="退出登录">
              <LogOut size={16} />
              <span>退出登录</span>
            </button>
          </div>
        </header>
        <main id="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function CenteredState({ children }: { children: ReactNode }) {
  return <div className="fullscreen-state">{children}</div>;
}
